/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DateTimeApiService } from '../../http';
import { DropdownModel } from '../../../../../core/models';
import { LogService } from '../../../../../core/services';

@Component({
    selector: 'app-loadbalancer-timezone',
    templateUrl: './timezone.component.html',
    styleUrls: ['./timezone.component.scss']
})

export class TimeZoneComponent implements OnInit {
    /**
     * User option to have one or two dropdowns.
     */
    @Input() public singleSelector: boolean = false;

    /**
     * Selected item index.
     */
    @Input() set timeZoneString(data) {
        this._timeZoneString = data;
    }

    /**
     * Used to disable a dropdown element.
     */
    @Input() public disabled: boolean = false;

    /**
     * selected item that will be sent to the parent component.
     */
    @Output() public timeZoneOut: EventEmitter<any> = new EventEmitter();

    /**
     * Region dropdown content array.
     */
    public regionDropdownContent: any [] = [];

    /**
     * Initial value used to set the dropdown index.
     */
    public initialTimeZone = {
        continent: null,
        region: null
    };

    /**
     * Used with input
     */
    private _timeZoneString: any;

    /**
     * Use with getter and setter.
     */
    private _continents: DropdownModel[] = [];

    /**
     * Use with getter and setter.
     */
    private _regions: any = {};

    constructor(
        private dateTimeApiService: DateTimeApiService,
        private logService: LogService
    ) {
    }

    get timeZoneString() {
        return this._timeZoneString;
    }

    get continents(): any {
        return this._continents;
    }

    set continents(continents) {
        this._continents = continents;
    }

    get regions(): any {
        return this._regions;
    }

    set regions(regions) {
        this._regions = regions;
    }

    public ngOnInit(): void {
        this.setUpPageData();
    }

    /**
     * Set the timezone (continent & region) for both dropdowns
     * @param $event
     */
    public setContinent($event) {
        if (!this.singleSelector) {
            // We must check if the continent has changed from load before updating the region and continent class variables.
            if ($event !== this.initialTimeZone.continent) {
                this.initialTimeZone.region = this.regions[$event] && this.regions[$event][0].key;
            }
            this.regionDropdownContent = this.regions[$event] && this.regions[$event].length ? this.regions[$event] : [];
        }

        this.initialTimeZone.continent = $event;
        this.timeZoneOut.emit(this.formatTimezoneOutValue());
    }

    /**
     * Set our timezone area
     * @param $event
     */
    public setTimeZoneArea($event: any) {
        this.initialTimeZone.region = $event;
        this.timeZoneOut.emit(this.formatTimezoneOutValue());
    }

    /**
     * Set-up page data and initialize dropdowns.
     * @private
     */
    private setUpPageData(): void {
        this.dateTimeApiService.getTimeZones()
            .subscribe(
                response => {
                    this.setUpInitialData();
                    this.dropDownSetUpHelper(response);
                },
                error => {
                    this.logService.warn('setUpPageData: ', error.message);
                });
    }

    /**
     * Set up initial timezone continent and region.
     * @private
     */
    private setUpInitialData(): void {
        if (this.singleSelector) {
            this.initialTimeZone.continent = this.timeZoneString;
        } else {
            const { continent, region } = this.getInitialTimeZoneParts(this.timeZoneString);
            this.initialTimeZone.continent = continent;
            this.initialTimeZone.region = region;
        }
    }

    /**
     * Get initial timezone parts.
     * @param timezone
     * @private
     */
    private getInitialTimeZoneParts(timezone): any {
        const timeZoneParts = timezone.split('/');

        return {
            continent: timeZoneParts.shift(),
            region: timeZoneParts.length > 1 ? timeZoneParts.join('/') : timeZoneParts[0]
        };
    }

    /**
     * Dropdown helper.
     * @param response
     * @private
     */
    private dropDownSetUpHelper(response): void {
        if (this.singleSelector) {
            this.setUpDropDown(response);
        } else {
            this.setUpDropDowns(response);
        }
    }

    /**
     * Set up single drop down content.
     * @param response
     * @private
     */
    private setUpDropDown(response) {
        const continents = [];
        for (const continent in response) {
            if (response.hasOwnProperty(continent)) {
                if (Object.keys(response[continent]).length) {
                    continents.push(...this.setContinentRegion(continent, response[continent]));
                } else {
                    continents.push(<DropdownModel>{ key: continent, value: continent });
                }
            }
        }
        this.continents = continents;
    }

    /**
     * Set the continent and region for the single drop down
     * @param continent
     * @param continentRegions
     * @private
     */
    private setContinentRegion(continent, continentRegions): DropdownModel[] {
        const continents: DropdownModel[] = [];
        for (const continentRegion in continentRegions) {
            if (continentRegions.hasOwnProperty(continentRegion)) {
                if (Object.keys(continentRegions[continentRegion]).length) {
                    continents.push(...this.setContinentRegion(continent + '/' + continentRegion, continentRegions[continentRegion]));
                } else {
                    continents.push(<DropdownModel>{
                        key: continent + '/' + continentRegion,
                        value: continent + '/' + continentRegion
                    });
                }
            }
        }

        return continents;
    }

    /**
     * Set-up continent and area for dropdowns.
     */
    private setUpDropDowns(response): void {
        const continents: DropdownModel[] = [];
        const regions: any = {};
        for (const continent in response) {
            if (response.hasOwnProperty(continent)) {
                continents.push(<DropdownModel>{ key: continent, value: continent });
                if (Object.keys(response[continent]).length) {
                    regions[continent] = this.setRegion(response[continent]);
                }
            }
        }
        this.continents = continents;
        this.regions = regions;
    }

    /**
     * Set the timezone region.
     * @param continentRegions
     * @private
     */
    private setRegion(continentRegions): DropdownModel[] {
        const regions = [];
        for (const region in continentRegions) {
            if (Object.keys(continentRegions[region]) && Object.keys(continentRegions[region]).length) {
                this.concatenateSubRegions(regions, region, Object.keys(continentRegions[region]));
            } else {
                regions.push(<DropdownModel>{ key: region, value: region });
            }
        }

        return regions;
    }

    /**
     * Concatenate the child regions for the second dropdown.
     * @param regions
     * @param region
     * @param childRegions
     * @private
     */
    private concatenateSubRegions(regions, region, childRegions): any {
        for (const childRegion of childRegions) {
            const concatenatedString = region + '/' + childRegion;
            regions.push({ key: concatenatedString, value: concatenatedString });
        }
    }

    /**
     * Format the region child elements if they exist.
     * @private
     */
    private formatTimezoneOutValue(): string {
        return this.initialTimeZone.continent + (this.initialTimeZone.region ? '/' + this.initialTimeZone.region : '');
    }
}
